import { ETemplateMessageCategory, getEFinancingProgramList, getEOriginSystemIdList } from '@if/core/types'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import {
  Box,
  Button,
  IconButton,
  Link as MuiLink,
  Stack,
  Typography
} from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { DataGrid, GridColDef, GridFooter, GridFooterContainer, GridRenderCellParams } from '@mui/x-data-grid'
import FullScreenContainer from '@src/components/FullScreenContainer'
import { useDeleteTemplateMessage, useGetTemplateMessagesAndPlaceholderskeys, useUpsertTemplateMessage } from '@src/data/api/chat-message/chat-message-api'
import { TemplateMessageRequestDto, TemplateMessageResponseDto } from '@src/data/types/TemplateMessageResponseDto'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@src/data/selectors/app-config-selectors'
import { useUser } from '@src/providers/UserProvider'
import TemplateMessageFilters from './components/TemplateMessageFilters'
import TemplateMessageModal from './components/TemplateMessageModal'


const DEFAULT_ROWS_PER_PAGE = 25

const ChatMessageManagementPage = () => {
  const featureFlags = useFeatureFlags()
  const user = useUser()
  const canManageMessage = user.rights.canManageMessage === true

  const { t } = useTranslation()

  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [selectedFinancingProgram, setSelectedFinancingProgram] = useState<string>('')
  const [selectedOriginSystem, setSelectedOriginSystem] = useState<string>('')
  const [editingMessage, setEditingMessage] = useState<TemplateMessageResponseDto | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isViewMode, setIsViewMode] = useState(false)
  const [paginationModel, setPaginationModel] = useState({ pageSize: DEFAULT_ROWS_PER_PAGE, page: 0 })
  const [templateMessagesWithPlaceholders] = useGetTemplateMessagesAndPlaceholderskeys()
  const allMessages: TemplateMessageResponseDto[] = useMemo(
    () => templateMessagesWithPlaceholders.messages ?? [],
    [templateMessagesWithPlaceholders]
  );
  const [upsertMessage, isUpserting, resetUpsert] = useUpsertTemplateMessage()
  const [deleteMessage, isDeleting] = useDeleteTemplateMessage()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [messageToDelete, setMessageToDelete] = useState<TemplateMessageResponseDto | null>(null)
  const [upsertError, setUpsertError] = useState<Error | null>(null)

  // Search after 300ms of inactivity
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)

    return () => clearTimeout(timer)
  }, [searchTerm])

  const lowerSearchTerm = useMemo(() => debouncedSearchTerm.toLowerCase(), [debouncedSearchTerm])

  const filteredMessages = useMemo(() => {
    let results =
      !selectedCategories.length || selectedCategories.includes('all')
        ? allMessages
        : allMessages.filter((msg) => selectedCategories.includes(msg.category))

    if (selectedFinancingProgram) {
      results = results.filter((msg) => msg.financingProgramIds?.includes(selectedFinancingProgram))
    }

    if (selectedOriginSystem) {
      results = results.filter((msg) => msg.originSystemIds?.includes(selectedOriginSystem))
    }

    if (lowerSearchTerm) {
      results = results.filter(
        (msg) =>
          msg.code.toLowerCase().includes(lowerSearchTerm) ||
          msg.messageFr.toLowerCase().includes(lowerSearchTerm) ||
          msg.messageEn.toLowerCase().includes(lowerSearchTerm)
      )
    }

    return results
  }, [allMessages, selectedCategories, selectedFinancingProgram, selectedOriginSystem, lowerSearchTerm])

  const paginatedMessages = useMemo(() => {
    const start = paginationModel.page * paginationModel.pageSize
    const end = start + paginationModel.pageSize
    return filteredMessages.slice(start, end)
  }, [filteredMessages, paginationModel])

  const handleSearch = useCallback((term: string) => {
    setSearchTerm(term)
    setPaginationModel({ pageSize: DEFAULT_ROWS_PER_PAGE, page: 0 })
  }, [])

  const handleCategoryChange = useCallback((categories: string[]) => {
    if (categories.includes('all')) {
      setSelectedCategories(['all'])
      setPaginationModel({ pageSize: DEFAULT_ROWS_PER_PAGE, page: 0 })
      return
    }
    setSelectedCategories(categories.filter(cat => cat !== 'all'))
    setPaginationModel({ pageSize: DEFAULT_ROWS_PER_PAGE, page: 0 })
  }, [])

  const handleFinancingProgramChange = useCallback((program: string) => {
    setSelectedFinancingProgram(program)
    setPaginationModel({ pageSize: DEFAULT_ROWS_PER_PAGE, page: 0 })
  }, [])

  const handleOriginSystemChange = useCallback((originSystem: string) => {
    setSelectedOriginSystem(originSystem)
    setPaginationModel({ pageSize: DEFAULT_ROWS_PER_PAGE, page: 0 })
  }, [])

  const handleCreateMessage = () => {
    resetUpsert()
    setUpsertError(null)
    setEditingMessage(null)
    setIsModalOpen(true)
  }

  const handleEditMessage = useCallback((message: TemplateMessageResponseDto) => {
    resetUpsert()
    setUpsertError(null)
    setEditingMessage(message)
    setIsViewMode(false)
    setIsModalOpen(true)
  }, [resetUpsert])

  const handleViewMessage = useCallback((message: TemplateMessageResponseDto) => {
    setEditingMessage(message)
    setIsViewMode(true)
    setIsModalOpen(true)
  }, [])

  const handleSaveMessage = async (data: TemplateMessageRequestDto) => {
    try {
      await upsertMessage(data, {
        onError: (error) => {
          setUpsertError(error)
        },
        onSuccess: () => {
          setIsModalOpen(false)
          setEditingMessage(null)
          setUpsertError(null)
        },
      })
      return true
    } catch {
      return false
    }
  }


  const columns = useMemo<GridColDef<TemplateMessageResponseDto>[]>(
    () => {
      return [
        {
          field: 'code',
          headerName: t('instantMessage.messageCode'),
          width: 120,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => {
            const row = params.row as TemplateMessageResponseDto
            return (
              <MuiLink
                component="button"
                variant="body2"
                onClick={() => handleViewMessage(row)}
                sx={{ cursor: 'pointer', textAlign: 'left' }}
              >
                {row.code}
              </MuiLink>
            )
          },
        },
        {
          field: 'messageFr',
          headerName: t('instantMessage.messageFr'),
          flex: 1,
          sortable: false,
        },
        {
          field: 'messageEn',
          headerName: t('instantMessage.messageEn'),
          flex: 1,
          sortable: false,
        },
        {
          field: 'category',
          headerName: t('instantMessage.category'),
          flex: 0.5,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => {
            const row = params.row as TemplateMessageResponseDto
            const category = t(`ETemplateMessageCategory.${row.category}`, { ns: 'enum', defaultValue: row.category })
            return category
          },
        },
        {
          field: 'originSystemIds',
          headerName: t('instantMessage.originSystem'),
          width: 150,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => {
            const row = params.row as TemplateMessageResponseDto
            return row.originSystemIds?.map((id) => t(`EOriginSystemId.${id}`, { ns: 'enum', defaultValue: id })).join(', ') || ''
          },
        },
        {
          field: 'financingProgramIds',
          headerName: t('instantMessage.financingProgram'),
          width: 180,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => {
            const row = params.row as TemplateMessageResponseDto
            return (row.financingProgramIds ?? []).map(id => t(`EFinancingProgram.${id}`, { ns: 'enum', defaultValue: id })).join(', ') || '-'
          },
        },
        {
          field: 'actions',
          headerName: t('common.actions'),
          width: 120,
          sortable: false,
          renderCell: (params: GridRenderCellParams) => {
            const row = params.row as TemplateMessageResponseDto
            return (
              <Stack direction="row" spacing={0.5}>
                <IconButton
                  size="small"
                  title={t('common.edit')}
                  disabled={!canManageMessage}
                  onClick={() => handleEditMessage(row)}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  size="small"
                  title={t('common.delete')}
                  disabled={!canManageMessage}
                  onClick={() => {
                    setMessageToDelete(row)
                    setDeleteDialogOpen(true)
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Stack>
            )
          },
        },
      ]
    },
    [t, handleViewMessage, handleEditMessage, canManageMessage],
  )

  const isRowSelectable = useCallback(() => false, [])

  const handlePreviousPage = useCallback(() => {
    setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }))
  }, [])

  const handleNextPage = useCallback(() => {
    setPaginationModel(prev => ({ ...prev, page: prev.page + 1 }))
  }, [])

  const CustomFooter = useMemo(() => {
    const CustomFooterComponent = () => {
      const startIndex = paginationModel.page * paginationModel.pageSize
      const endIndex = Math.min(startIndex + paginationModel.pageSize, filteredMessages.length)
      const isFirstPage = paginationModel.page === 0
      const isLastPage = endIndex >= filteredMessages.length

      return (
        <GridFooterContainer>
          <GridFooter sx={{ '& .MuiTablePagination-actions': { display: 'none' } }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2 }}>
            <Button disabled={isFirstPage} sx={{ mx: 1 }} onClick={handlePreviousPage}>
              {t('common.previous')}
            </Button>
            <Button disabled={isLastPage} sx={{ mx: 1 }} onClick={handleNextPage}>
              {t('common.next')}
            </Button>
          </Box>
        </GridFooterContainer>
      )
    }
    return CustomFooterComponent
  }, [paginationModel, filteredMessages.length, t, handlePreviousPage, handleNextPage])

  if (!featureFlags.useInstantMessage) return <Navigate to="/applications/browse" replace />

  return (
    <FullScreenContainer>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" component="h1">
          {t('instantMessage.messageManager')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <TemplateMessageFilters
          onSearch={handleSearch}
          onCategoryChange={handleCategoryChange}
          onFinancingProgramChange={handleFinancingProgramChange}
          onOriginSystemChange={handleOriginSystemChange}
          selectedCategories={selectedCategories}
          selectedFinancingProgram={selectedFinancingProgram}
          selectedOriginSystem={selectedOriginSystem}
          categories={Object.values(ETemplateMessageCategory)}
          categoryLabels={Object.fromEntries(
            Object.values(ETemplateMessageCategory).map((cat) => [cat, t(`ETemplateMessageCategory.${cat}`, { ns: 'enum', defaultValue: cat })])
          )}
          financingPrograms={Object.fromEntries(
            getEFinancingProgramList().map((prog) => [prog, t(`EFinancingProgram.${prog}`, { ns: 'enum', defaultValue: prog })])
          )}
          originSystems={Object.fromEntries(
            getEOriginSystemIdList().map((sys) => [sys, t(`EOriginSystemId.${sys}`, { ns: 'enum', defaultValue: sys })])
          )}
        />
      </Box>

      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateMessage} disabled={!canManageMessage}>
            {t('instantMessage.createMessage')}
          </Button>
        </Box>
        <Box sx={{ height: '45rem', width: '100%' }}>
          <DataGrid
            disableColumnFilter
            disableVirtualization={true}
            isRowSelectable={isRowSelectable}
            rows={paginatedMessages}
            columns={columns}
            getRowId={(row: TemplateMessageResponseDto) => row.id}
            getRowClassName={(params) => params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'}
            paginationModel={paginationModel}
            onPaginationModelChange={({ pageSize }: { pageSize: number }) => {
              if (pageSize !== paginationModel.pageSize) {
                setPaginationModel({ pageSize, page: 0 })
              }
            }}
            loading={isUpserting}
            paginationMode="server"
            rowCount={filteredMessages.length}
            slots={{
              footer: CustomFooter,
            }}
            slotProps={{
              pagination: {
                labelRowsPerPage: t('common.customDisplayedRowsTextFooter.RowsPerPage'),
                labelDisplayedRows: () => {
                  const from = filteredMessages.length === 0 ? 0 : paginationModel.page * paginationModel.pageSize + 1
                  const to = Math.min(
                    filteredMessages.length,
                    (paginationModel.page + 1) * paginationModel.pageSize
                  )

                  return t('common.customDisplayedRowsTextFooter.displayRange', {
                    defaultValue: '',
                    from,
                    to,
                  })
                },
              },
            }}
          />
        </Box>
      </Box>

      <TemplateMessageModal
        open={isModalOpen}
        message={editingMessage}
        isViewMode={isViewMode}
        canSave={canManageMessage}
        onCancel={() => {
          resetUpsert()
          setIsModalOpen(false)
          setEditingMessage(null)
          setIsViewMode(false)
          setUpsertError(null)
        }}
        onConfirm={handleSaveMessage}
        isLoading={isUpserting}
        apiError={upsertError}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('common.confirmDelete')}</DialogTitle>
        <DialogContent>
          <Typography>{t('instantMessage.confirmDelete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            color="error"
            onClick={async () => {
              if (messageToDelete) {
                await deleteMessage(messageToDelete.code, {
                  onSuccess: () => {
                    setDeleteDialogOpen(false)
                    setMessageToDelete(null)
                  },
                })
              }
            }}
            disabled={isDeleting}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </FullScreenContainer>
  )
}

export default memo(ChatMessageManagementPage)
